import { useCallback, useEffect, useState } from "react"

import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    IconButton,
    TextField,
    Tooltip,
} from "@mui/material"

import SupportAgentIcon from "@mui/icons-material/SupportAgent"
import WhatsAppIcon from "@mui/icons-material/WhatsApp"
import VisibilityIcon from "@mui/icons-material/Visibility"
import LogoutIcon from "@mui/icons-material/Logout"
import InsightsIcon from "@mui/icons-material/Insights"

import api from "../../api"
import { getAdminToken, saveAdminToken, clearAdminToken } from "../../auth"

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DemoContainer } from "@mui/x-date-pickers/internals/demo"

import MetricsLogin from "./MetricsLogin"
import Loading from "../Loading"
import useNotification from "../Notification"
import AllCharts from "../Dash/HistoricDash/graphs/charts"
import WhatsApp from "./WhatsApp"
import Usage from "./Usage"
import DashboardChartVisibilityDialog from "../Dialogs/DashboardChartVisibility"

import { translations } from "../Dialogs/DashboardChartVisibility"

import dayjs from "dayjs"

import "./styles.css"


const MetricsGrid = () => {
    const [loading, setLoading] = useState(false)
    const [token, setToken] = useState(getAdminToken())

    const [selectedMenu, setSelectedMenu] = useState("atendimento")

    // Atendimento filters
    const [enterprise, setEnterprise] = useState("__all__")
    const [enterprises, setEnterprises] = useState([])
    const [periodStart, setPeriodStart] = useState(
        new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            1
        )
    )
    const [periodEnd, setPeriodEnd] = useState(new Date())

    const [showVisibilityDialog, setShowVisibilityDialog] = useState(false)
    const [data, setData] = useState({})

    const [visibleCharts, setVisibleCharts] = useState(() => {
        const stored = localStorage.getItem("historic-visible-charts")

        return stored
            ? JSON.parse(stored)
            : Object.keys(translations).reduce((acc, key) => {
                acc[key] = true
                return acc
            }, {})
    })


    const handleLogin = useCallback((password) => {
        setLoading(true)

        fetch("/api/auth/token/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                username: "admin",
                password: password
            })
        })
            .then((res) => {
                setLoading(false)

                if (res.status === 200)
                    return res.json()

                if (res.status === 401) {
                    useNotification(
                        "Atenção",
                        "A senha informada não está correta!",
                        "danger"
                    )

                    return
                }

                throw res
            })
            .then((data) => {
                if (!data)
                    return

                setToken(data.access)
                saveAdminToken(data.access)
            })
            .catch((err) => {
                console.error(err)
            })
    }, [])


    const fetchEnterprises = useCallback(async () => {
        setLoading(true)

        try {
            const res = await api.get("api/enterprises/", {
                params: {
                    metrics: true
                }
            })

            if (res.status === 200) {
                setEnterprises(res.data)
                return
            }

            useNotification(
                "Ops",
                "Algo deu errado ao buscar as empresas.",
                "danger"
            )
        } catch (err) {
            console.error(err)

            useNotification(
                "Ops",
                "Algo deu errado ao buscar as empresas.",
                "danger"
            )
        } finally {
            setLoading(false)
        }
    }, [])


    const fetchData = useCallback(async () => {
        setLoading(true)

        try {
            const res = await api.get("api/metrics/", {
                params: {
                    enterprise: enterprise,
                    start: dayjs(periodStart).format("YYYY-MM-DD"),
                    end: dayjs(periodEnd).format("YYYY-MM-DD")
                }
            })

            if (res.status === 200) {
                setData(res.data)
                return
            }

            useNotification(
                "Ops",
                "Algo deu errado ao buscar as métricas.",
                "danger"
            )
        } catch (err) {
            console.error(err)

            useNotification(
                "Ops",
                "Algo deu errado ao buscar as métricas.",
                "danger"
            )
        } finally {
            setLoading(false)
        }
    }, [enterprise, periodStart, periodEnd])


    useEffect(() => {
        if (!token || selectedMenu !== "atendimento")
            return

        fetchEnterprises()
        fetchData()
    }, [token, selectedMenu, fetchEnterprises, fetchData])


    if (!token) {
        return (
            <>
                <Loading loading={loading} />

                <MetricsLogin
                    onClickFn={handleLogin}
                    loading={loading}
                />
            </>
        )
    }


    const handleLogout = () => {
        clearAdminToken()
        localStorage.removeItem("@kwikapi-admin-username")
        window.location.href = "/metrics"
    }


    return (
        <>
            <Loading loading={loading} />

            <Box
                display="flex"
                width="100%"
                height="100%"
                minHeight={0}
            >

                {/* Left navigation */}
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    width="60px"
                    minWidth="60px"
                    height="100%"
                    paddingTop={2}
                    borderRight="1px solid #E2E2E2"
                    backgroundColor="#FAFAFA"
                >

                    {/* Atendimento */}
                    <Tooltip
                        title="Atendimento"
                        placement="right"
                    >
                        <IconButton
                            onClick={() =>
                                setSelectedMenu("atendimento")
                            }
                            sx={{
                                width: "44px",
                                height: "44px",
                                borderRadius: "8px",
                                mb: 1,
                                color:
                                    selectedMenu === "atendimento"
                                        ? "#1976D2"
                                        : "#6c757d",
                                backgroundColor:
                                    selectedMenu === "atendimento"
                                        ? "#EAF2FF"
                                        : "transparent",
                                "&:hover": {
                                    backgroundColor: "#EAF2FF"
                                }
                            }}
                        >
                            <SupportAgentIcon />
                        </IconButton>
                    </Tooltip>


                    {/* Consumo WhatsApp */}
                    <Tooltip
                        title="Consumo WhatsApp"
                        placement="right"
                    >
                        <IconButton
                            onClick={() =>
                                setSelectedMenu("whatsapp")
                            }
                            sx={{
                                width: "44px",
                                height: "44px",
                                borderRadius: "8px",
                                color:
                                    selectedMenu === "whatsapp"
                                        ? "#25D366"
                                        : "#6c757d",
                                backgroundColor:
                                    selectedMenu === "whatsapp"
                                        ? "#EAF8EF"
                                        : "transparent",
                                "&:hover": {
                                    backgroundColor: "#EAF8EF"
                                }
                            }}
                        >
                            <WhatsAppIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Utilização da plataforma" placement="right">
                        <IconButton
                            aria-label="Utilização da plataforma"
                            onClick={() => setSelectedMenu("usage")}
                            sx={{
                                width: "44px",
                                height: "44px",
                                borderRadius: "8px",
                                mt: 1,
                                color: selectedMenu === "usage" ? "#1976D2" : "#6c757d",
                                backgroundColor: selectedMenu === "usage" ? "#EAF2FF" : "transparent",
                                "&:hover": { backgroundColor: "#EAF2FF" }
                            }}
                        >
                            <InsightsIcon />
                        </IconButton>
                    </Tooltip>

                    {/* Logoff */}
                    <Tooltip
                        title="Sair"
                        placement="right"
                    >
                        <IconButton
                            onClick={handleLogout}
                            sx={{
                                width: "44px",
                                height: "44px",
                                borderRadius: "8px",
                                mt: "auto",
                                mb: 2,
                                color: "#6c757d",
                                "&:hover": {
                                    color: "#d32f2f",
                                    backgroundColor: "#FDECEC"
                                }
                            }}
                        >
                            <LogoutIcon />
                        </IconButton>
                    </Tooltip>
                </Box>


                {/* Main content */}
                <Box
                    display="flex"
                    flexDirection="column"
                    flex={1}
                    minWidth={0}
                    minHeight={0}
                >

                    {/* ========================= */}
                    {/* ATENDIMENTO                 */}
                    {/* ========================= */}

                    {selectedMenu === "atendimento" && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            flex={1}
                            minWidth={0}
                            minHeight={0}
                        >

                            {/* Atendimento filters */}
                            <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                gap={2}
                                padding={2}
                            >

                                {/* Empresa */}
                                <FormControl
                                    size="small"
                                    sx={{
                                        width: "15rem"
                                    }}
                                >
                                    <Autocomplete
                                        size="small"
                                        options={[
                                            {
                                                id: "__all__",
                                                name: "Todas as Empresas"
                                            },
                                            ...[...enterprises].sort(
                                                (a, b) =>
                                                    a.name.localeCompare(
                                                        b.name
                                                    )
                                            )
                                        ]}
                                        getOptionLabel={(option) =>
                                            option.name
                                        }
                                        value={
                                            enterprise === "__all__"
                                                ? {
                                                    id: "__all__",
                                                    name: "Todas as Empresas"
                                                }
                                                : enterprises.find(
                                                    (e) =>
                                                        e.id === enterprise
                                                ) || null
                                        }
                                        onChange={(event, newValue) => {
                                            setEnterprise(
                                                newValue
                                                    ? newValue.id
                                                    : ""
                                            )
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Empresa"
                                            />
                                        )}
                                    />
                                </FormControl>


                                {/* Datas */}
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DemoContainer
                                        components={[
                                            "DatePicker",
                                            "DatePicker"
                                        ]}
                                        sx={{
                                            marginTop: "-8px"
                                        }}
                                    >

                                        <DatePicker
                                            label="Início"
                                            value={
                                                periodStart
                                                    ? dayjs(periodStart)
                                                    : null
                                            }
                                            onChange={(newValue) =>
                                                setPeriodStart(newValue)
                                            }
                                            slotProps={{
                                                textField: {
                                                    size: "small"
                                                }
                                            }}
                                            sx={{
                                                width: "10rem"
                                            }}
                                        />

                                        <DatePicker
                                            label="Fim"
                                            value={
                                                periodEnd
                                                    ? dayjs(periodEnd)
                                                    : null
                                            }
                                            onChange={(newValue) =>
                                                setPeriodEnd(newValue)
                                            }
                                            slotProps={{
                                                textField: {
                                                    size: "small"
                                                }
                                            }}
                                            sx={{
                                                width: "10rem"
                                            }}
                                        />

                                    </DemoContainer>
                                </LocalizationProvider>


                                {/* Buscar dados */}
                                <Button
                                    variant="contained"
                                    onClick={fetchData}
                                >
                                    Buscar Dados
                                </Button>


                                {/* Visibility */}
                                <IconButton
                                    onClick={() =>
                                        setShowVisibilityDialog(true)
                                    }
                                >
                                    <VisibilityIcon />
                                </IconButton>

                            </Box>


                            {/* Atendimento charts */}
                            <Box
                                flex={1}
                                minHeight={0}
                                minWidth={0}
                                p={2}
                                pt={0}
                                overflow="auto"
                            >
                                <AllCharts
                                    data={data}
                                    mode="period"
                                    interval="period"
                                    last_date={null}
                                    visibleCharts={visibleCharts}
                                />
                            </Box>


                            {/* Chart visibility dialog */}
                            {showVisibilityDialog && (
                                <DashboardChartVisibilityDialog
                                    open={showVisibilityDialog}
                                    close={() =>
                                        setShowVisibilityDialog(false)
                                    }
                                    visibleCharts={visibleCharts}
                                    setVisibleCharts={setVisibleCharts}
                                />
                            )}

                        </Box>
                    )}


                    {/* ========================= */}
                    {/* CONSUMO WHATSAPP           */}
                    {/* ========================= */}

                    {selectedMenu === "whatsapp" && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            flex={1}
                            minWidth={0}
                            minHeight={0}
                        >
                            <WhatsApp />
                        </Box>
                    )}

                    {selectedMenu === "usage" && (
                        <Usage
                            periodStart={periodStart}
                            periodEnd={periodEnd}
                            onStartChange={setPeriodStart}
                            onEndChange={setPeriodEnd}
                        />
                    )}

                </Box>

            </Box>
        </>
    )
}


export default MetricsGrid